import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/contexts/AuthContext';
import { profileAPI, resolveImageUrl } from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, Upload, X, AlertCircle } from 'lucide-react';

const MAX_BIO_LENGTH = 140;
const MAX_AVATAR_SIZE_MB = 3;
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/jpg'];

export function ProfileEdit() {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const fileInputRef = useRef(null);

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  // Form state
  const [name, setName] = useState('');
  const [bio, setBio] = useState('');
  const [cleanSince, setCleanSince] = useState('');
  const [cleanDaysPrivacy, setCleanDaysPrivacy] = useState(true);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);

  // Validation
  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const response = await profileAPI.getProfile();
      const profileData = response.data;
      
      setProfile(profileData);
      setName(profileData.name);
      setBio(profileData.bio || '');
      setCleanSince(profileData.clean_since || '');
      setCleanDaysPrivacy(profileData.clean_days_privacy);
      setAvatarUrl(profileData.avatar_url);
    } catch (error) {
      console.error('Failed to load profile:', error);
      toast.error('Erro ao carregar perfil');
      navigate('/me');
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      toast.error('Formato inválido. Use JPG ou PNG');
      return;
    }

    // Validate file size
    if (file.size > MAX_AVATAR_SIZE_MB * 1024 * 1024) {
      toast.error(`Arquivo muito grande. Máximo ${MAX_AVATAR_SIZE_MB}MB`);
      return;
    }

    // Create preview
    const previewUrl = URL.createObjectURL(file);
    setAvatarPreview(previewUrl);

    // Upload
    setUploadingAvatar(true);
    try {
      const response = await profileAPI.uploadAvatar(file);
      setAvatarUrl(response.data.avatar_url);
      toast.success('Avatar atualizado!');
    } catch (error) {
      console.error('Failed to upload avatar:', error);
      toast.error(error.response?.data?.detail || 'Erro ao fazer upload');
      setAvatarPreview(null);
    } finally {
      setUploadingAvatar(false);
    }
  };

  const removeAvatarPreview = () => {
    if (avatarPreview) {
      URL.revokeObjectURL(avatarPreview);
      setAvatarPreview(null);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    } else if (name.length < 2) {
      newErrors.name = 'Nome deve ter pelo menos 2 caracteres';
    }

    if (bio.length > MAX_BIO_LENGTH) {
      newErrors.bio = `Bio não pode ter mais de ${MAX_BIO_LENGTH} caracteres`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Por favor, corrija os erros no formulário');
      return;
    }

    setSaving(true);
    try {
      const updateData = {
        name: name.trim(),
        bio: bio.trim() || null,
        clean_since: cleanSince || null,
        clean_days_privacy: cleanDaysPrivacy,
      };

      const response = await profileAPI.updateProfile(updateData);
      
      // Update user in AuthContext
      updateUser(response.data);
      
      toast.success('Perfil atualizado com sucesso!');
      navigate('/me');
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error(error.response?.data?.detail || 'Erro ao atualizar perfil');
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = () => {
    if (!profile) return false;
    
    return (
      name !== profile.name ||
      bio !== (profile.bio || '') ||
      cleanSince !== (profile.clean_since || '') ||
      cleanDaysPrivacy !== profile.clean_days_privacy
    );
  };

  const getInitials = (name) => {
    return name
      ?.split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const remainingChars = MAX_BIO_LENGTH - bio.length;
  const isOverLimit = remainingChars < 0;

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto py-6 px-4">
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto py-6 px-4" data-testid="profile-edit-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Editar Perfil</h1>
        <p className="text-muted-foreground">
          Atualize suas informações pessoais e preferências
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Avatar Section */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Foto de Perfil</CardTitle>
            <CardDescription>
              Escolha uma imagem JPG ou PNG de até {MAX_AVATAR_SIZE_MB}MB
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-6">
              <Avatar className="h-24 w-24 border-2 border-border" data-testid="edit-avatar-preview">
                <AvatarImage src={avatarPreview || resolveImageUrl(avatarUrl)} alt={name} />
                <AvatarFallback className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white text-2xl">
                  {getInitials(name)}
                </AvatarFallback>
              </Avatar>

              <div className="flex-1">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/jpg"
                  onChange={handleAvatarChange}
                  className="hidden"
                  data-testid="avatar-input"
                  aria-label="Upload de avatar"
                />
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingAvatar}
                    data-testid="upload-avatar-button"
                  >
                    {uploadingAvatar ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Escolher Imagem
                      </>
                    )}
                  </Button>
                  {avatarPreview && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={removeAvatarPreview}
                      data-testid="remove-avatar-preview"
                      aria-label="Remover preview"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Basic Info */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Informações Básicas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Name */}
            <div>
              <Label htmlFor="name">
                Nome <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Seu nome"
                data-testid="name-input"
                aria-invalid={!!errors.name}
                aria-describedby={errors.name ? 'name-error' : undefined}
              />
              {errors.name && (
                <p id="name-error" className="text-sm text-red-500 mt-1 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {errors.name}
                </p>
              )}
            </div>

            {/* Bio */}
            <div>
              <Label htmlFor="bio">Bio</Label>
              <Textarea
                id="bio"
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                placeholder="Conte um pouco sobre você..."
                className={`min-h-[100px] resize-none ${isOverLimit ? 'border-red-500' : ''}`}
                data-testid="bio-textarea"
                aria-invalid={!!errors.bio}
                aria-describedby="bio-counter"
              />
              <div className="flex items-center justify-between mt-1">
                {errors.bio && (
                  <p className="text-sm text-red-500 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {errors.bio}
                  </p>
                )}
                <div className="flex-1" />
                <p
                  id="bio-counter"
                  className={`text-sm ${
                    isOverLimit
                      ? 'text-red-500'
                      : remainingChars < 20
                      ? 'text-yellow-600'
                      : 'text-muted-foreground'
                  }`}
                >
                  {remainingChars} caracteres restantes
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Dias Limpos */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Dias Limpos</CardTitle>
            <CardDescription>
              Informe a data em que iniciou sua jornada de sobriedade. Os dias são calculados automaticamente.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="clean-since">Limpo desde</Label>
              <Input
                id="clean-since"
                type="date"
                max={new Date().toISOString().split('T')[0]}
                value={cleanSince}
                onChange={(e) => setCleanSince(e.target.value)}
                data-testid="clean-since-input"
              />
              {cleanSince && (
                <p className="text-sm text-green-600 mt-2 font-medium" data-testid="clean-days-display">
                  {Math.max(0, Math.floor((Date.now() - new Date(cleanSince + 'T00:00:00Z').getTime()) / 86400000))} dias limpo
                </p>
              )}
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="clean-days-privacy">Mostrar dias limpos publicamente</Label>
                <p className="text-sm text-muted-foreground">
                  Outros membros poderão ver sua contagem
                </p>
              </div>
              <Switch
                id="clean-days-privacy"
                checked={cleanDaysPrivacy}
                onCheckedChange={setCleanDaysPrivacy}
                data-testid="clean-days-privacy-toggle"
                aria-label="Alternar privacidade dos dias limpos"
              />
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/me')}
            disabled={saving}
            data-testid="cancel-button"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={!hasChanges() || saving || isOverLimit}
            data-testid="save-button"
            className="flex-1"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Salvando...
              </>
            ) : (
              'Salvar Alterações'
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
